import { Chip } from "@dash/form";
import { RowCellContent, Table, TableBody } from "@dash/table";
import { useTheme } from "@emotion/react";
import { MdCheckCircle, MdPallet } from "react-icons/md";

import { ItemDoc } from "@megaron/docs-contracts";
import { Order } from "@megaron/fulfillment-contracts";
import { OrderDisplayFulfillmentStatus } from "@megaron/shopify";

import { statusTitleMap } from "./packing-page/PackageList";

type Props = {
  orders: Order[];
  isLoading: boolean;
  itemDocs: ItemDoc[];
};

export type ChipProps = {
  color: string;
  variant: "primary" | "secondary" | "outline";
};

export const getOrderPackingStatus = (order: Order): string => {
  if (order.cancelledAt) return "anulowane";
  if (order.fulfillmentStatus === OrderDisplayFulfillmentStatus.OnHold) return "anulowane";
  if (order.fulfillmentStatus === OrderDisplayFulfillmentStatus.Fulfilled) return "zrealizowane";
  if (order.packages.length === 0) return "niezapakowane";
  if (order.packages.every((p) => p.isLabelScanned)) return "zapakowane";
  return "rozpoczęto pakowanie";
};

export const OrdersTable: React.FC<Props> = ({ orders, isLoading, itemDocs }) => {
  const theme = useTheme();

  const getChipProps = (status: string): ChipProps => {
    if (status === "anulowane") return { color: theme.colors.danger, variant: "secondary" };
    if (status === "zrealizowane") return { color: theme.colors.success, variant: "primary" };
    if (status === "niezapakowane") return { color: theme.colors.default, variant: "outline" };
    if (status === "zapakowane") return { color: theme.colors.success, variant: "secondary" };
    if (status === "rozpoczęto pakowanie") return { color: theme.colors.warning, variant: "secondary" };
    return { color: theme.colors.default, variant: "outline" };
  };

  const getProductChipProps = (): ChipProps => ({ color: theme.colors.primary, variant: "primary" });

  const tableColumns = [
    {},
    ...tableHeadingList.map(
      ({ heading }) =>
        ({
          isSortable: false,
          label: heading,
        } as const),
    ),
  ];

  const normalizeGtin = (s?: string | null) => (s ?? "").replace(/\D/g, "").replace(/^0+/, "");

  const getDisplayNameFromDocs = (barcodeOrGtin: string | undefined | null, fallbackName: string) => {
    const code = normalizeGtin(barcodeOrGtin ?? null);
    if (!code) return fallbackName;
    const match = itemDocs.find((x: any) => normalizeGtin(x.gtin) === code);
    return match?.itemName ?? fallbackName;
  };

  const formatProductsToPack = (order: Order): { label: string }[] => {
    const items = (order as any).items ?? [];
    return items.map((i: any) => {
      const qty = (i.quantityToPack ?? i.toPack ?? i.quantity ?? 0) as number;
      const fallback = (i.shortName ?? i.name ?? i.title ?? "") as string;
      const name = getDisplayNameFromDocs(i.barcode ?? (i as any)?.gtin ?? null, fallback);
      return { label: `${name} (${qty})` };
    });
  };

  const getTableRowCellsContent = (order: Order): RowCellContent[] => {
    const status = getOrderPackingStatus(order);
    const chip = getChipProps(status);
    const inpostStatuses = order.packages.map(
      (p) => statusTitleMap[p.trackingStatus as string] ?? p.trackingStatus ?? "brak",
    );
    const productsToPack = formatProductsToPack(order);
    const productChip = getProductChipProps();

    return [
      {
        isLink: true,
        element: (
          <div
            css={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        ),
      },
      {
        isLink: true,
        element: (
          <div css={{ display: "flex", alignItems: "center", gap: 4 }}>
            {`WEB-${order.orderNumber}`}
            {order.deliveryMethod == "palletDelivery" && <MdPallet />}
          </div>
        ),
      },
      {
        isLink: true,
        element: (
          <>
            {order.processedAt.toLocaleString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </>
        ),
      },
      {
        isLink: true,
        element: (
          <Chip color={chip.color} variant={chip.variant}>
            {status}
          </Chip>
        ),
      },
      {
        isLink: true,
        element: (
          <div css={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {productsToPack.length === 0 ? (
              <Chip color={productChip.color} variant={productChip.variant}>
                brak
              </Chip>
            ) : (
              productsToPack.map((p, idx) => (
                <Chip key={idx} color={productChip.color} variant={productChip.variant}>
                  {p.label}
                </Chip>
              ))
            )}
          </div>
        ),
      },
      {
        isLink: true,
        element: (
          <div css={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {inpostStatuses.length === 0 ? <span></span> : inpostStatuses.map((s, idx) => <span key={idx}>{s}</span>)}
          </div>
        ),
      },
      {
        isLink: true,
        element: (() => {
          const hasDispatchOrder = order.packages.some((p) => p.dispatchOrderId);
          const isPacked = order.packages.length > 0 && order.packages.every((p) => p.isLabelScanned);
          const waitingForPickup = isPacked && !hasDispatchOrder;

          if (hasDispatchOrder) {
            return (
              <div css={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "12px", color: theme.colors.success }}>
                <MdCheckCircle size={14} />
                <span>Odbior zlecony</span>
              </div>
            );
          }

          if (waitingForPickup) {
            return (
              <div css={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "12px", color: theme.colors.warning }}>
                <span>Oczekuje na odbiór</span>
              </div>
            );
          }

          return <span></span>;
        })(),
      },
    ];
  };

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table columns={tableColumns} css={{ padding: "0 2rem" }}>
        <TableBody
          isLoading={isLoading}
          rows={orders.map((order) => ({
            uuid: order.id,
            linkTo: `/specjalista/order-fulfillment/id/${order.id}`,
            cellsContent: getTableRowCellsContent(order),
            css: {
              "&:hover": {
                "td:nth-child(1)": {
                  textDecoration: "underline",
                },
              },
            },
          }))}
        />
      </Table>
    </div>
  );
};

const tableHeadingList: { heading: string }[] = [
  { heading: "Numer" },
  { heading: "Data" },
  { heading: "Status" },
  { heading: "Produkty" },
  { heading: "Status InPost" },
  { heading: "Odbior" },
];
